import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import AdminLayout from "@/components/feature/AdminLayout";
import AdminHeader from "@/components/feature/AdminHeader";
import { requireAuth } from "@/middleware/authGuard";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";
import { logActivity } from "@/lib/logActivity";
import { createNotification } from "@/lib/createNotification";
import ClientModal from "./components/ClientModal";
import MessagesModal from "./components/MessagesModal";
import AddOnsModal from "./components/AddOnsModal";
import LoadingState from "@/components/base/LoadingState";
import ErrorState from "@/components/base/ErrorState";
import StatusBadge from "@/components/base/StatusBadge";
import UpgradeModal from "@/components/base/UpgradeModal";
import { checkLimit, bumpLimitCache, type LimitCheckResult } from "@/lib/planLimits";

type Client = {
  id: string;
  tenant_id: string;
  full_name: string;
  email: string;
  phone: string;
  address: string;
  notes: string;
  status: string;
  created_at: string;
  updated_at: string;
};



export default function AdminClients() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingNew, setPendingNew] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [msgClient, setMsgClient] = useState<Client | null>(null);
  const [msgOpen, setMsgOpen] = useState(false);
  const [addonClient, setAddonClient] = useState<Client | null>(null);
  const [addonOpen, setAddonOpen] = useState(false);
  const [upgradeResult, setUpgradeResult] = useState<LimitCheckResult | null>(null);

  useEffect(() => {
    if (searchParams.get("new") === "1") setPendingNew(true);
  }, [searchParams]);

  useEffect(() => {
    if (!loading && pendingNew) { setPendingNew(false); handleNew(); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, pendingNew]);

  const fetchClients = useCallback(async (tid: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchErr } = await supabase
        .from("clients")
        .select("*")
        .eq("tenant_id", tid)
        .order("created_at", { ascending: false });
      if (fetchErr) throw new Error(fetchErr.message);
      if (data) setClients(data as Client[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load clients.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const isAuth = await requireAuth();
      if (!isAuth) { navigate("/login"); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid) fetchClients(tid);
      else setLoading(false);
    };
    init();
  }, [navigate, fetchClients]);

  const filtered = clients.filter((c) => {
    const matchSearch =
      c.full_name.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || c.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const handleEdit = (client: Client) => {
    setSelectedClient(client);
    setIsNew(false);
    setModalOpen(true);
  };

  const handleNew = async () => {
    const limitResult = await checkLimit('users');
    if (limitResult && !limitResult.allowed) {
      setUpgradeResult(limitResult);
      return;
    }
    setSelectedClient(null);
    setIsNew(true);
    setModalOpen(true);
  };

  const handleToggleStatus = async (client: Client) => {
    if (!tenantId) return;
    const isActive = client.status === "active";
    const newStatus = isActive ? "inactive" : "active";
    const result = await Swal.fire({
      title: isActive ? "Deactivate client?" : "Reactivate client?",
      text: isActive
        ? `"${client.full_name}" will be marked as inactive.`
        : `"${client.full_name}" will be marked as active again.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: isActive ? "Deactivate" : "Reactivate",
      cancelButtonText: "Cancel",
      confirmButtonColor: isActive ? "#ef4444" : "#C8A96E",
      cancelButtonColor: "#64748b",
      background: "#ffffff",
      color: "#0f172a",
    });
    if (!result.isConfirmed) return;
    const { error } = await supabase
      .from("clients")
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq("id", client.id)
      .eq("tenant_id", tenantId);
    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" });
      return;
    }
    setClients((prev) => prev.map((c) => c.id === client.id ? { ...c, status: newStatus } : c));
    logActivity({ tenantId, action: "updated", entity: "client", entityId: client.id, entityName: client.full_name });
    Swal.fire({ title: isActive ? "Deactivated" : "Reactivated", icon: "success", timer: 1200, showConfirmButton: false, background: "#ffffff", color: "#0f172a" });
  };

  const handleSave = async (data: Client) => {
    if (!tenantId) return;
    if (isNew) {
      const { id: _id, ...insertData } = data;
      const { data: created, error } = await supabase
        .from("clients")
        .insert({ ...insertData, tenant_id: tenantId })
        .select()
        .maybeSingle();
      if (!error && created) {
        setClients((prev) => [created as Client, ...prev]);
        bumpLimitCache('users');
        logActivity({ tenantId, action: "created", entity: "client", entityId: (created as Client).id, entityName: (created as Client).full_name });
        createNotification({ tenantId, type: 'success', title: 'New client added', message: `Client "${(created as Client).full_name}" was successfully added.` });
      }
    } else {
      const { data: updated, error } = await supabase
        .from("clients")
        .update({ ...data, updated_at: new Date().toISOString() })
        .eq("id", data.id)
        .eq("tenant_id", tenantId)
        .select()
        .maybeSingle();
      if (!error && updated) {
        setClients((prev) => prev.map((c) => (c.id === (updated as Client).id ? (updated as Client) : c)));
        logActivity({ tenantId, action: "updated", entity: "client", entityId: data.id, entityName: data.full_name });
      }
    }
    setModalOpen(false);
  };

  return (
    <AdminLayout>
      <AdminHeader
        title="Clients"
        subtitle="Manage your studio clients"
        actions={
          <button
            onClick={handleNew}
            className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line"></i>
            </span>
            New Client
          </button>
        }
      />
      <div className="px-4 md:px-8 py-4 md:py-7">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search clients..."
              className="w-full bg-white rounded-xl border border-slate-200 pl-9 pr-4 py-2.5 text-sm text-slate-800 placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors"
            />
          </div>
          <div className="flex items-center gap-1 bg-white rounded-xl border border-slate-200 p-1">
            {["all", "active", "inactive", "archived"].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-colors cursor-pointer whitespace-nowrap ${
                  statusFilter === s ? "bg-[#C8A96E] text-white" : "text-slate-400 hover:text-slate-700"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <span className="text-slate-400 text-sm">{filtered.length} results</span>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <LoadingState />
          ) : error ? (
            <ErrorState message={error} onRetry={() => tenantId && fetchClients(tenantId)} showLogin />
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Client</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Contact</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Address</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Status</th>
                  <th className="text-left text-slate-400 text-xs font-medium px-5 py-3.5 uppercase tracking-wider">Since</th>
                  <th className="px-5 py-3.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map((client) => (
                  <tr key={client.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#C8A96E]/10 text-[#9a7840] font-semibold text-sm shrink-0">
                          {client.full_name.charAt(0)}
                        </div>
                        <div>
                          <p className="text-[#0f172a] text-sm font-medium">{client.full_name}</p>
                          <p className="text-slate-400 text-xs font-mono truncate max-w-[120px]">{client.id.slice(0, 8)}…</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3.5">
                      <p className="text-slate-600 text-sm">{client.email}</p>
                      <p className="text-slate-400 text-xs">{client.phone}</p>
                    </td>
                    <td className="px-5 py-3.5">
                      <p className="text-slate-400 text-sm truncate max-w-[180px]">{client.address}</p>
                    </td>
                    <td className="px-5 py-3.5">
                      <StatusBadge status={client.status} />
                    </td>
                    <td className="px-5 py-3.5">
                      <p className="text-slate-400 text-xs">
                        {new Date(client.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </p>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => { setAddonClient(client); setAddonOpen(true); }}
                          className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all cursor-pointer"
                          title="Add-ons"
                        >
                          <i className="ri-vip-crown-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => { setMsgClient(client); setMsgOpen(true); }}
                          className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#9a7840] hover:bg-[#C8A96E]/10 rounded-lg transition-all cursor-pointer"
                          title="Messages"
                        >
                          <i className="ri-chat-3-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => handleEdit(client)}
                          className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
                          title="Edit"
                        >
                          <i className="ri-pencil-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => navigate(`/admin/clients/${client.id}`)}
                          className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#9a7840] hover:bg-[#C8A96E]/10 rounded-lg transition-all cursor-pointer"
                          title="View detail"
                        >
                          <i className="ri-arrow-right-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => handleToggleStatus(client)}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all cursor-pointer ${
                            client.status === "active"
                              ? "text-slate-400 hover:text-red-500 hover:bg-red-50"
                              : "text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"
                          }`}
                          title={client.status === "active" ? "Deactivate" : "Reactivate"}
                        >
                          <i className={`text-sm ${client.status === "active" ? "ri-user-forbid-line" : "ri-user-follow-line"}`}></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && !loading && (
                  <tr>
                    <td colSpan={6} className="py-16">
                      <div className="flex flex-col items-center gap-3 text-center">
                        <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#C8A96E]/10">
                          <i className="ri-user-3-line text-[#9a7840] text-2xl"></i>
                        </div>
                        <div>
                          <p className="text-[#0f172a] font-medium text-sm mb-1">
                            {clients.length === 0 ? "No clients yet" : "No clients found"}
                          </p>
                          <p className="text-slate-400 text-xs">
                            {clients.length === 0
                              ? "Add your first client to start managing bookings."
                              : "Try adjusting your search or filters."}
                          </p>
                        </div>
                        {clients.length === 0 && (
                          <button
                            onClick={handleNew}
                            className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold px-5 py-2 rounded-lg mt-1 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-add-line"></i>
                            Add First Client
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>

      {modalOpen && (
        <ClientModal
          client={selectedClient}
          isNew={isNew}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
        />
      )}
      {msgOpen && msgClient && tenantId && (
        <MessagesModal
          tenantId={tenantId}
          clientId={msgClient.id}
          clientName={msgClient.full_name}
          onClose={() => { setMsgOpen(false); setMsgClient(null); }}
        />
      )}
      {addonOpen && addonClient && (
        <AddOnsModal
          client={addonClient}
          onClose={() => { setAddonOpen(false); setAddonClient(null); }}
        />
      )}
      {upgradeResult && (
        <UpgradeModal
          result={upgradeResult}
          onClose={() => setUpgradeResult(null)}
        />
      )}
    </AdminLayout>
  );
}
